'use client';

import {
  Fragment,
  forwardRef,
  ReactNode,
  createContext,
  useContext,
} from 'react';
import {
  Listbox,
  ListboxButton,
  Label,
  ListboxOptions,
  ListboxOption,
} from '@headlessui/react';
import { ChevronDownIcon, CheckIcon } from 'lucide-react';
import { cn } from '../../lib/utils';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps {
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  name?: string;
  children?: ReactNode;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  options?: SelectOption[];
}

export interface SelectTriggerProps {
  className?: string;
  children?: ReactNode;
}

export interface SelectValueProps {
  placeholder?: string;
  className?: string;
}

export interface SelectContentProps {
  className?: string;
  children?: ReactNode;
}

export interface SelectItemProps {
  value: string;
  className?: string;
  disabled?: boolean;
  children?: ReactNode;
}

type SelectContextType = {
  value: string | undefined;
};

const SelectContext = createContext<SelectContextType>({
  value: undefined,
});

export function useSelectContext() {
  return useContext(SelectContext);
}

export const Select = ({
  children,
  value,
  onChange,
  placeholder,
  options,
  className,
}: SelectProps) => {
  return (
    <SelectContext.Provider value={{ value: value }}>
      <Listbox value={value} onChange={onChange}>
        <div className={cn('relative', className)}>{children}</div>
      </Listbox>
    </SelectContext.Provider>
  );
};

const SelectTrigger = forwardRef<
  HTMLButtonElement,
  SelectTriggerProps
>(({ className, children }, ref) => {
  return (
    <ListboxButton
      ref={ref}
      className={cn(
        'relative w-full h-10 flex items-center justify-between',
        'border border-input rounded-md px-3 py-2 text-sm',
        'bg-background text-foreground placeholder:text-muted-foreground',
        'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50',
        className
      )}
    >
      {children}
      <ChevronDownIcon className="h-4 w-4 opacity-50" />
    </ListboxButton>
  );
});
SelectTrigger.displayName = 'SelectTrigger';

const SelectValue = ({
  placeholder,
  className,
}: SelectValueProps) => {
  const { value } = useSelectContext();
  return (
    <span className={cn('block truncate', className)}>
      {value || placeholder || 'Select...'}
    </span>
  );
};

const SelectContent = ({
  className,
  children,
}: SelectContentProps) => {
  return (
    <ListboxOptions
      className={cn(
        'absolute z-50 mt-1 max-h-60 w-full overflow-auto',
        'rounded-md bg-background py-1 text-base shadow-lg',
        'ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm',
        className
      )}
    >
      {children}
    </ListboxOptions>
  );
};

const SelectItem = forwardRef<HTMLLIElement, SelectItemProps>(
  ({ value, className, disabled, children }, ref) => {
    return (
      <ListboxOption
        ref={ref}
        value={value}
        disabled={disabled}
        as={Fragment}
      >
        {({ active, selected }) => (
          <li
            className={cn(
              'relative cursor-default select-none py-2 pl-10 pr-4',
              active
                ? 'bg-primary/10 text-primary'
                : 'text-foreground',
              disabled && 'opacity-50 cursor-not-allowed',
              className
            )}
          >
            {selected && (
              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-primary">
                <CheckIcon className="h-5 w-5" aria-hidden="true" />
              </span>
            )}
            {children || value}
          </li>
        )}
      </ListboxOption>
    );
  }
);
SelectItem.displayName = 'SelectItem';

export { SelectTrigger, SelectValue, SelectContent, SelectItem };
